import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowUp, Copy, CornerDownLeft, Loader2, Table2, X } from 'lucide-react';
import { AuroraLogo, OFFICE_APPS } from './icons';
import {
  askAuroraAI, errorMessage, insertAIResult, markdownTableRows, markdownToHtml, readEditorContext,
} from './officeAI';

const SUGGESTIONS = {
  doc: ['Resume este documento', '¿Qué le falta a este documento?', 'Sugiere un mejor título', 'Escribe preguntas frecuentes sobre el contenido'],
  sheet: ['Resume estos datos', '¿Qué tendencias ves?', 'Detecta errores o datos atípicos', '¿Qué gráficos me recomiendas?'],
  pdf: ['Resume este PDF', '¿Qué obligaciones o fechas importantes tiene?', 'Explícamelo en palabras sencillas', '¿Hay algo que deba revisar con cuidado?'],
  slides: ['Resume la presentación', '¿Qué diapositivas faltan?', 'Mejora la estructura de la historia', 'Escribe un guion para presentarla'],
};

/** Panel lateral de Aurora IA: conversación con el contenido del archivo abierto. */
export default function AIPanel({ kind, fileId, bridge, canEdit, userName, onClose }) {
  const app = OFFICE_APPS[kind] || OFFICE_APPS.doc;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async (value) => {
    const text = (value ?? input).trim();
    if (!text || busy) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const ctx = await readEditorContext(bridge, kind).catch(() => ({ context: '' }));
      const res = await askAuroraAI({
        file_id: fileId,
        kind,
        action: 'chat',
        messages: next.filter((m) => !m.error).map(({ role, content }) => ({ role, content })),
        context: ctx.context || '',
        selection: ctx.selection || '',
      });
      setMessages([...next, { role: 'assistant', content: res.text }]);
    } catch (err) {
      setMessages([...next, { role: 'assistant', content: errorMessage(err), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const insert = async (text, asTable = false) => {
    try {
      await insertAIResult(bridge, { type: 'markdown', text, asTable }, 'cursor');
      toast.success('Insertado');
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo insertar'));
    }
  };

  const firstName = (userName || '').split(' ')[0];

  return (
    <aside className="zo-ai-panel" data-testid="zo-ai-panel">
      <div className="zo-ai-panel-head">
        <AuroraLogo size={22} />
        <div className="zo-ai-panel-title">
          <strong>Aurora IA</strong>
          <span>En {app.name}</span>
        </div>
        <button type="button" className="zo-icon-btn" onClick={onClose} aria-label="Cerrar panel"><X className="h-4 w-4" /></button>
      </div>

      <div className="zo-ai-panel-list" ref={listRef}>
        {messages.length === 0 && (
          <div className="zo-ai-hello">
            <div className="zo-ai-hello-title">
              Hola{firstName ? `, ${firstName}` : ''}.
              <br />
              ¿Qué hacemos con {app.thisNoun}?
            </div>
            <p>Aurora IA lee {app.theNoun} para responder, resumir o redactar contenido que puedes insertar.</p>
            <div className="zo-ai-suggestions">
              {(SUGGESTIONS[kind] || SUGGESTIONS.doc).map((s) => (
                <button key={s} type="button" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={`zo-msg zo-msg-${m.role} ${m.error ? 'is-error' : ''}`}>
            {m.role === 'user' ? (
              <div className="zo-msg-bubble">{m.content}</div>
            ) : (
              <>
                {/* markdownToHtml escapa todo el texto antes de agregar etiquetas */}
                <div className="zo-md" dangerouslySetInnerHTML={{ __html: markdownToHtml(m.content) }} />
                {!m.error && (
                  <div className="zo-msg-actions">
                    <button type="button" onClick={() => copy(m.content)}><Copy className="h-3.5 w-3.5" /> Copiar</button>
                    {canEdit && kind === 'doc' && (
                      <button type="button" onClick={() => insert(m.content)}><CornerDownLeft className="h-3.5 w-3.5" /> Insertar</button>
                    )}
                    {canEdit && kind === 'sheet' && markdownTableRows(m.content).length > 1 && (
                      <button type="button" onClick={() => insert(m.content, true)}><Table2 className="h-3.5 w-3.5" /> Insertar tabla</button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {busy && (
          <div className="zo-msg zo-msg-assistant">
            <div className="zo-typing"><span /><span /><span /></div>
          </div>
        )}
      </div>

      <form
        className="zo-ai-panel-input"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea
          ref={inputRef}
          rows={2}
          value={input}
          placeholder={`Pregunta sobre ${app.thisNoun}…`}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button type="submit" className="zo-send" disabled={!input.trim() || busy} aria-label="Enviar">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
      <p className="zo-ai-disclaimer">Aurora IA puede equivocarse. Revisa la información importante.</p>
    </aside>
  );
}
